import React from "react";
import Paper from "@material-ui/core/Paper";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import toastr from "toastr";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      width: "100%",
      maxWidth: 1300,
      margin: "30px auto",
      padding: theme.spacing(3),
      border: "2px solid black",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    uploadContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    form: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: theme.spacing(2),
      boxSizing: "border-box",
    },
    inputField: {
      width: "100%",
      maxWidth: 500,
    },
    radioGroup: {
      flexDirection: "row",
    },
    radioLabel: {
      marginBottom: theme.spacing(1),
    },
    imagePreview: {
      width: "100px",
      height: "100px",
      marginTop: theme.spacing(2),
    },
    button: {
      marginTop: theme.spacing(2),
      width: "100%",
      maxWidth: 300,
    },
    sectionDivider: {
      width: "100%",
      maxWidth: 500,
      borderTop: "1px solid #ccc",
      margin: theme.spacing(2, 0),
    },
    taxTableContainer: {
      width: "100%",
      maxWidth: "100%",
      overflowX: "auto",
      marginTop: theme.spacing(1),
    },
    taxTableCell: {
      minWidth: 160,
      verticalAlign: "top",
    },
    maxRowsNote: {
      width: "100%",
      maxWidth: 900,
      textAlign: "right",
      color: "#999",
      fontSize: 12,
    },
  })
);

const incomeRanges = [
  "Less than 1 lac",
  "1 lac to 5 lacs",
  "5 lacs to 10 lacs",
  "10 lacs to 25 lacs",
  "More than 25 lacs",
];

const qualification = [
  "Below Graduate",
  "Graduate",
  "Post Graduate",
  "Professional Qualification",
  "Other",
];

const differentlyAbledStatus = ["Not Applicable", "Differently Abled"];

const typeOfImpairment = [
  "Blindness",
  "Low Vision",
  "Hearing Impairment",
  "Locomotor Disability",
  "Leprosy Cured",
  "Cerebral Palsy",
  "Intellectual Disability",
  "Mental Illness",
  "Muscular Dystrophy",
  "Parkinson's Disease",
  "Acid Attack Victim",
  "Sickle Cell Disease",
  "Hemophilia",
  "Thalassemia",
  "Speech and Language Disability",
  "Multiple Sclerosis",
  "Specific Learning Disabilities",
  "Chronic Neurological Conditions",
  "Autism Spectrum Disorder",
  "Dwarfism",
];


const identificationTypes = ["TIN", "Equivalent"];

export {
  incomeRanges,
  qualification,
  differentlyAbledStatus,
  typeOfImpairment,
};

const emptyTaxResidencyRow = {
  account_holder_name: "",
  account_holder_details: "",
  country_of_tax_residency: "",
  tax_identification_number: "",
  identification_type: "",
  is_us_citizen_or_green_card_holder: false,
};

type TaxRowErrors = {
  account_holder_name?: string;
  account_holder_details?: string;
  country_of_tax_residency?: string;
  tax_identification_number?: string;
  identification_type?: string;
};

const validateTaxRow = (row: any): TaxRowErrors => {
  const errors: TaxRowErrors = {};
  if (!row.account_holder_name?.trim())
    errors.account_holder_name = "Required";
  if (!row.account_holder_details?.trim())
    errors.account_holder_details = "Required";
  if (!row.country_of_tax_residency?.trim())
    errors.country_of_tax_residency = "Required";
  if (!row.tax_identification_number?.trim()) {
    errors.tax_identification_number = "Required";
  } else if (
    !/^[A-Za-z0-9-]{4,20}$/.test(row.tax_identification_number.trim())
  ) {
    errors.tax_identification_number =
      "Enter a valid TIN (4-20 alphanumeric chars)";
  }
  if (!row.identification_type) errors.identification_type = "Required";
  return errors;
};

interface PersonalDetailsProps {
  addPersonalDetails: (event: any) => void;
  accountType: string;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}


const PersonalDetails: React.FC<PersonalDetailsProps> = ({
  addPersonalDetails,
  accountType,
  formData,
  setFormData,
}) => {
  const classes = useStyles();
  const isNRI = accountType === "individual_non_resident";
  const taxRows = formData.tax_residency_details || [];

  const [taxRowErrors, setTaxRowErrors] = React.useState<TaxRowErrors[]>([]);
  const [taxSectionError, setTaxSectionError] = React.useState<string>("");

  const handleInputChange =
    (key: keyof typeof formData) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        if (key === "differentlyAbledStatus" && value === "Not Applicable") {
          setFormData({
            ...formData,
            differentlyAbledStatus: value,
            typeOfImpairment: "",
            percentageOfImpairment: "",
            UDIDNumber: "",
          });
        } else {
          setFormData({ ...formData, [key]: value });
        }
      };

  const handleRadioChange =
    (key: keyof typeof formData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [key]: event.target.value });
      };

  const handleBooleanRadioChange =
    (key: keyof typeof formData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === "Yes";
        const updates: any = { ...formData, [key]: value };

        if (key === "is_merchant_navy_nri" && !value) {
          updates.merchant_navy_document_type = "";
          updates.merchant_navy_document = null;
        }
        if (key === "tax_resident_other_country" && !value) {
          updates.tax_residency_details = [];
          setTaxRowErrors([]);
          setTaxSectionError("");
        }
        setFormData(updates);
      };

  const handleImageUpload =
    (key: keyof typeof formData) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
          setFormData({ ...formData, [key]: event.target.files[0] });
        }
      };

  const getBase64FileInfo = (base64: string) => {
    if (base64.startsWith("JVBERi0")) {
      return { mime: "application/pdf", ext: "pdf", isPdf: true };
    }
    if (base64.startsWith("/9j/")) {
      return { mime: "image/jpeg", ext: "jpg", isPdf: false };
    }
    if (base64.startsWith("iVBORw0KGgo")) {
      return { mime: "image/png", ext: "png", isPdf: false };
    }
    return { mime: "application/octet-stream", ext: "bin", isPdf: false };
  };

  const handleDownloadFile = (file: File | string, fileName: string) => {
    let url = "";
    let downloadName = fileName;

    if (file instanceof File) {
      url = URL.createObjectURL(file);
      downloadName = file.name || fileName;
    } else {
      const { mime, ext } = getBase64FileInfo(file);
      url = `data:${mime};base64,${file}`;
      downloadName = `${fileName}.${ext}`;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (file instanceof File) {
      URL.revokeObjectURL(url);
    }
  };

  const addTaxResidencyRow = () => {
    if (taxRows.length >= 3) return;
    setFormData({
      ...formData,
      tax_residency_details: [...taxRows, { ...emptyTaxResidencyRow }],
    });
    setTaxRowErrors([...taxRowErrors, {}]);
    setTaxSectionError("");
  };

  const removeTaxResidencyRow = (index: number) => {
    const updated = taxRows.filter((_: any, i: number) => i !== index);
    setFormData({ ...formData, tax_residency_details: updated });
    setTaxRowErrors(taxRowErrors.filter((_, i) => i !== index));
  };

  const updateTaxResidencyRow = (
    index: number,
    key: keyof typeof emptyTaxResidencyRow,
    value: any
  ) => {
    const updated = taxRows.map((row: any, i: number) =>
      i === index ? { ...row, [key]: value } : row
    );
    setFormData({ ...formData, tax_residency_details: updated });

    setTaxRowErrors((prev) => {
      const next = [...prev];
      if (next[index]) {
        next[index] = { ...next[index], [key]: undefined };
      }
      return next;
    });
    setTaxSectionError("");
  };

  const handleSave = (event: any) => {
    if (isNRI) {
      if (
        formData.sebi_action === null ||
        formData.sebi_action === undefined
      ) {
        toastr.error("Please select SEBI Action.");
        return;
      }

      if (!formData.investment_trading_experience) {
        toastr.error("Please enter Investment / Trading Experience.");
        return;
      }

      if (!/^\d+$/.test(formData.investment_trading_experience)) {
        toastr.error(
          "Investment / Trading Experience should contain only years."
        );
        return;
      }

      if (!formData.status) {
        toastr.error("Please select Status.");
        return;
      }

      if (
        formData.is_merchant_navy_nri &&
        !formData.merchant_navy_document_type
      ) {
        toastr.error("Please select Merchant Navy Document Type.");
        return;
      }

      if (
        formData.is_merchant_navy_nri &&
        !formData.merchant_navy_document
      ) {
        toastr.error("Please upload Merchant Navy Document.");
        return;
      }

      if (formData.tax_resident_other_country) {
        if (taxRows.length === 0) {
          toastr.error("Please add at least one Tax Residency Detail.");
          return;
        }

        if (taxRows.length > 3) {
          toastr.error("Maximum 3 Tax Residency Details are allowed.");
          return;
        }

        const allErrors = taxRows.map((row: any) => validateTaxRow(row));
        const hasErrors = allErrors.some(
          (e: any) => Object.keys(e).length > 0
        );

        if (hasErrors) {
          setTaxRowErrors(allErrors);
          toastr.error("Please complete all Tax Residency Details.");
          return;
        }
      }
    }

    addPersonalDetails(event);
  };

  return (
    <Paper className={classes.paper}>
      <form className={classes.form}>
        <TextField
          className={classes.inputField}
          label="Differently Abled Status"
          select
          value={formData.differentlyAbledStatus}
          onChange={handleInputChange("differentlyAbledStatus")}
        >
          {differentlyAbledStatus.map((item, index) => (
            <MenuItem value={item} key={index}>
              {item}
            </MenuItem>
          ))}
        </TextField>

        {formData.differentlyAbledStatus === "Differently Abled" && (
          <>
            <TextField
              className={classes.inputField}
              label="Type Of Impairment"
              select
              value={formData.typeOfImpairment}
              onChange={handleInputChange("typeOfImpairment")}
            >
              {typeOfImpairment.map((item, index) => (
                <MenuItem value={item} key={index}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <div style={{ display: "flex", width: "100%" }}>
              <div style={{ flex: 1, marginLeft: "15%" }}>
                <TextField
                  className={classes.inputField}
                  label="Percentage Of Impairment"
                  type="number"
                  value={formData.percentageOfImpairment}
                  onChange={(e) => {
                    let value = e.target.value;
                    if (/^\d{0,3}(\.\d{0,2})?$/.test(value) || value === "") {
                      let num = parseFloat(value);
                      if (!isNaN(num) && num > 100) {
                        num = 100;
                      }
                      setFormData({
                        ...formData,
                        percentageOfImpairment:
                          value === "" ? "" : num.toString(),
                      });
                    }
                  }}
                  inputProps={{ step: "0.01", min: 0, max: 100 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  }}
                />
              </div>
              <div style={{ flex: 1, marginRight: "3%" }}>
                <TextField
                  className={classes.inputField}
                  label="UDID Number"
                  value={formData.UDIDNumber}
                  onChange={handleInputChange("UDIDNumber")}
                />
              </div>
            </div>
          </>
        )}

        <TextField
          className={classes.inputField}
          label="Marital Status"
          select
          value={formData.maritalStatus}
          onChange={handleInputChange("maritalStatus")}
        >
          <MenuItem value="Single">Single</MenuItem>
          <MenuItem value="Married">Married</MenuItem>
        </TextField>

        <div className={classes.radioLabel}>Are you Politically Exposed?</div>
        <RadioGroup
          aria-label="Politically Exposed"
          name="politicallyExposed"
          value={formData.politicallyExposed ? "Yes" : "No"}
          onChange={handleRadioChange("politicallyExposed")}
          className={classes.radioGroup}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio color="primary" />}
            label="Yes"
          />
          <FormControlLabel
            value="No"
            control={<Radio color="primary" />}
            label="No"
          />
        </RadioGroup>

        <TextField
          className={classes.inputField}
          label="Birth City"
          value={formData.birthCity}
          onChange={handleInputChange("birthCity")}
        />

        <TextField
          className={classes.inputField}
          label="Birth Country"
          value={formData.birthCountry}
          onChange={handleInputChange("birthCountry")}
        />

        <TextField
          className={classes.inputField}
          label="Nationality"
          value={formData.nationality}
          onChange={handleInputChange("nationality")}
        />

        <TextField
          className={classes.inputField}
          label="Maiden Name"
          value={formData.maidenName}
          onChange={handleInputChange("maidenName")}
        />
        <TextField
          className={classes.inputField}
          label="Mother's Name"
          value={formData.mothersName}
          onChange={handleInputChange("mothersName")}
        />
        <TextField
          className={classes.inputField}
          label="Annual Income"
          select
          value={formData.annualIncome}
          onChange={handleInputChange("annualIncome")}
        >
          {incomeRanges.map((item, index) => (
            <MenuItem value={item} key={index}>
              {item}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className={classes.inputField}
          label="Qualification"
          select
          value={formData.qualification}
          onChange={handleInputChange("qualification")}
        >
          {qualification.map((item, index) => (
            <MenuItem value={item} key={index}>
              {item}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className={classes.inputField}
          label="Gender"
          select
          value={formData.gender}
          onChange={handleInputChange("gender")}
        >
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Transgender">Transgender</MenuItem>
        </TextField>
        <TextField
          className={classes.inputField}
          label="Occupation"
          value={formData.occupation}
          onChange={handleInputChange("occupation")}
        />

        <div className={classes.uploadContainer}>
          <label htmlFor="wetSign">Wet Signature</label>
          {formData.wetSignImage && (
            <IconButton
              size="small"
              sx={{ color: "green" }}
              onClick={() =>
                handleDownloadFile(formData.wetSignImage, "wet-signature")
              }
            >
              <DownloadIcon />
            </IconButton>
          )}
          <input
            name="wetSign"
            type="file"
            accept="image/*"
            onChange={handleImageUpload("wetSignImage")}
          />
          {formData.wetSignImage && (
            <img
              className={classes.imagePreview}
              src={
                formData.wetSignImage instanceof File
                  ? URL.createObjectURL(formData.wetSignImage)
                  : `data:image/png;base64,${formData.wetSignImage}`
              }
              alt="Wet Sign Preview"
            />
          )}
        </div>

        <div className={classes.uploadContainer}>
          <label htmlFor="passportPhoto">Passport Photo</label>
          {formData.passportPhoto && (
            <IconButton
              size="small"
              sx={{ color: "green" }}
              onClick={() =>
                handleDownloadFile(formData.passportPhoto, "passport-photo")
              }
            >
              <DownloadIcon />
            </IconButton>
          )}
          <input
            name="passportPhoto"
            type="file"
            accept="image/*"
            onChange={handleImageUpload("passportPhoto")}
          />
          {formData.passportPhoto && (
            <img
              className={classes.imagePreview}
              src={
                formData.passportPhoto instanceof File
                  ? URL.createObjectURL(formData.passportPhoto)
                  : `data:image/png;base64,${formData.passportPhoto}`
              }
              alt="Passport Photo Preview"
            />
          )}
        </div>

        {isNRI && (
          <>
            <div className={classes.sectionDivider} />

            <div className={classes.radioLabel}>
              Have you ever been subject to SEBI action?
            </div>
            <RadioGroup
              value={formData.sebi_action ? "Yes" : "No"}
              onChange={handleBooleanRadioChange("sebi_action")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label="No"
              />
            </RadioGroup>

            <TextField
              className={classes.inputField}
              label="Investment / Trading Experience (Years)"
              type="number"
              value={formData.investment_trading_experience}
              onChange={(e) => {
                const value = e.target.value;

                if (/^\d*$/.test(value)) {
                  setFormData({
                    ...formData,
                    investment_trading_experience: value,
                  });
                }
              }}
              inputProps={{
                min: 0,
                max: 99,
              }}
            />

            <TextField
              select
              className={classes.inputField}
              label="Status"
              value={formData.status}
              onChange={handleInputChange("status")}
            >
              <MenuItem value="resident_individual">
                Resident Individual
              </MenuItem>
              <MenuItem value="non_resident_individual">
                Non Resident Individual
              </MenuItem>
              <MenuItem value="foreign_national">
                Foreign National
              </MenuItem>
            </TextField>

            <div className={classes.radioLabel}>
              Are you Merchant Navy NRI?
            </div>
            <RadioGroup
              value={formData.is_merchant_navy_nri ? "Yes" : "No"}
              onChange={handleBooleanRadioChange("is_merchant_navy_nri")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label="No"
              />
            </RadioGroup>

            {formData.is_merchant_navy_nri && (
              <>
                <TextField
                  select
                  className={classes.inputField}
                  label="Document Type"
                  value={formData.merchant_navy_document_type}
                  onChange={handleInputChange("merchant_navy_document_type")}
                >
                  <MenuItem value="mariner_declaration">
                    Mariner Declaration
                  </MenuItem>
                  <MenuItem value="cdc">CDC</MenuItem>
                </TextField>

                <div className={classes.uploadContainer}>
                  <label htmlFor="merchantNavyDoc">
                    Merchant Navy Document
                  </label>
                  {formData.merchant_navy_document && (
                    <IconButton
                      size="small"
                      sx={{ color: "green" }}
                      onClick={() =>
                        handleDownloadFile(
                          formData.merchant_navy_document,
                          "merchant-navy-document"
                        )
                      }
                    >
                      <DownloadIcon />
                    </IconButton>
                  )}
                  <input
                    name="merchantNavyDoc"
                    type="file"
                    accept="image/*,.pdf,application/pdf"
                    onChange={handleImageUpload("merchant_navy_document")}
                  />
                  {formData.merchant_navy_document && (() => {
                    const file = formData.merchant_navy_document;
                    if (file instanceof File) {
                      const fileUrl = URL.createObjectURL(file);
                      if (file.type === "application/pdf") {
                        return (
                          <iframe
                            src={fileUrl}
                            title="Merchant Navy Document"
                            width="300"
                            height="400"
                            style={{ border: "1px solid #ccc", marginTop: 10 }}
                          />
                        );
                      }
                      return (
                        <img
                          className={classes.imagePreview}
                          src={fileUrl}
                          alt="Merchant Navy Document"
                        />
                      );
                    }
                    const isPdf = file.startsWith("JVBERi0");
                    if (isPdf) {
                      return (
                        <iframe
                          src={`data:application/pdf;base64,${file}`}
                          title="Merchant Navy Document"
                          width="300"
                          height="400"
                          style={{ border: "1px solid #ccc", marginTop: 10 }}
                        />
                      );
                    }
                    return (
                      <img
                        className={classes.imagePreview}
                        src={`data:image/png;base64,${file}`}
                        alt="Merchant Navy Document"
                      />
                    );
                  })()}
                </div>
              </>
            )}

            <div className={classes.radioLabel}>
              Tax Resident of another country?
            </div>
            <RadioGroup
              value={formData.tax_resident_other_country ? "Yes" : "No"}
              onChange={handleBooleanRadioChange("tax_resident_other_country")}
              className={classes.radioGroup}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label="No"
              />
            </RadioGroup>

            {formData.tax_resident_other_country && (
              <>
                <TableContainer className={classes.taxTableContainer}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account Holder Name</TableCell>
                        <TableCell>Account Holder Details</TableCell>
                        <TableCell>Country of Tax Residency</TableCell>
                        <TableCell>Tax Identification Number</TableCell>
                        <TableCell>
                          Identification Type (TIN or Equivalent)
                        </TableCell>
                        <TableCell>US Citizen / Green Card Holder?</TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              taxRows.length >= 3
                                ? "Maximum 3 entries allowed"
                                : "Add tax residency detail"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={taxRows.length >= 3}
                                onClick={addTaxResidencyRow}
                              >
                                <AddCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taxRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No tax residency details added. Click + to add one.
                          </TableCell>
                        </TableRow>
                      )}
                      {taxRows.map((row: any, index: number) => {
                        const errs = taxRowErrors[index] || {};
                        return (
                          <TableRow key={index}>
                            <TableCell className={classes.taxTableCell}>
                              <TextField
                                fullWidth
                                size="small"
                                value={row.account_holder_name}
                                error={!!errs.account_holder_name}
                                helperText={errs.account_holder_name}
                                onChange={(e) =>
                                  updateTaxResidencyRow(
                                    index,
                                    "account_holder_name",
                                    e.target.value
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className={classes.taxTableCell}>
                              <TextField
                                fullWidth
                                size="small"
                                value={row.account_holder_details}
                                error={!!errs.account_holder_details}
                                helperText={errs.account_holder_details}
                                onChange={(e) =>
                                  updateTaxResidencyRow(
                                    index,
                                    "account_holder_details",
                                    e.target.value
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className={classes.taxTableCell}>
                              <TextField
                                fullWidth
                                size="small"
                                value={row.country_of_tax_residency}
                                error={!!errs.country_of_tax_residency}
                                helperText={errs.country_of_tax_residency}
                                onChange={(e) =>
                                  updateTaxResidencyRow(
                                    index,
                                    "country_of_tax_residency",
                                    e.target.value
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className={classes.taxTableCell}>
                              <TextField
                                fullWidth
                                size="small"
                                value={row.tax_identification_number}
                                error={!!errs.tax_identification_number}
                                helperText={errs.tax_identification_number}
                                onChange={(e) =>
                                  updateTaxResidencyRow(
                                    index,
                                    "tax_identification_number",
                                    e.target.value
                                  )
                                }
                              />
                            </TableCell>
                            <TableCell className={classes.taxTableCell}>
                              <TextField
                                select
                                fullWidth
                                size="small"
                                value={row.identification_type}
                                error={!!errs.identification_type}
                                helperText={errs.identification_type}
                                onChange={(e) =>
                                  updateTaxResidencyRow(
                                    index,
                                    "identification_type",
                                    e.target.value
                                  )
                                }
                              >
                                {identificationTypes.map((item, i) => (
                                  <MenuItem value={item} key={i}>
                                    {item}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell className={classes.taxTableCell}>
                              <RadioGroup
                                row
                                value={
                                  row.is_us_citizen_or_green_card_holder
                                    ? "Yes"
                                    : "No"
                                }
                                onChange={(e) =>
                                  updateTaxResidencyRow(
                                    index,
                                    "is_us_citizen_or_green_card_holder",
                                    e.target.value === "Yes"
                                  )
                                }
                              >
                                <FormControlLabel
                                  value="Yes"
                                  control={
                                    <Radio size="small" color="primary" />
                                  }
                                  label="Yes"
                                />
                                <FormControlLabel
                                  value="No"
                                  control={
                                    <Radio size="small" color="primary" />
                                  }
                                  label="No"
                                />
                              </RadioGroup>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => removeTaxResidencyRow(index)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {taxRows.length >= 3 && (
                  <div className={classes.maxRowsNote}>
                    Maximum of 3 tax residency details reached.
                  </div>
                )}

                {taxSectionError && (
                  <div
                    style={{ width: "90%", color: "#d32f2f", fontSize: 13 }}
                  >
                    {taxSectionError}
                  </div>
                )}
              </>
            )}
          </>
        )}

        <Button
          className={classes.button}
          variant="contained"
          color="primary"
          onClick={handleSave}
        >
          Save
        </Button>
      </form>
    </Paper>
  );
};

export default PersonalDetails;